'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import Badge from '../common/Badge'
import { pickerMax, type TimedUITicketType, type DayTabConfig } from '@/lib/timed-ui/adapters'
import { toDateInTz } from '@/lib/analytics/date-utils'
import { ticketSaleStatusLabel } from '@/lib/ticket-sale-status'

function ExpandableDescription({ html }: { html: string }) {
  const [expanded, setExpanded] = useState(false)
  const [clamped, setClamped] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (el) {
      // Check if content overflows 2 lines (~2.5em at text-xs)
      setClamped(el.scrollHeight > el.clientHeight + 1)
    }
  }, [html])

  return (
    <div className="mt-1">
      <div
        ref={ref}
        className={`text-xs text-text-tertiary prose prose-xs max-w-none ${!expanded ? 'line-clamp-2' : ''}`}
        dangerouslySetInnerHTML={{ __html: html }}
      />
      {clamped && (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="flex items-center gap-1 mt-1 text-xs font-medium text-primary hover:text-primary-hover transition-colors"
        >
          <span>{expanded ? 'Show less' : 'Show more'}</span>
          <svg
            className={`w-3 h-3 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  )
}

interface TicketSelectorProps {
  ticketTypes: TimedUITicketType[]
  selectedTickets: Record<string, number>
  onQuantityChange: (ticketTypeId: string, quantity: number) => void
  slotCapacity?: number
  dayTabConfig?: DayTabConfig | null
  /** Event IANA timezone — used to decide which day-tab is "today". */
  timezone?: string
  /** A cap on tickets across ALL types in the order (a sponsor link's per-order limit). */
  orderLimit?: number | null
}

const TicketSelector = ({ ticketTypes, onQuantityChange, selectedTickets = {}, slotCapacity, dayTabConfig, timezone, orderLimit }: TicketSelectorProps) => {
  const formatPrice = useFormatPrice()
  const [quantities, setQuantities] = useState<Record<string, number>>({})

  // Day tab logic: only use tabs when dayTabConfig is enabled with 2+ tabs
  const hasDayTabs = !!(dayTabConfig?.enabled && dayTabConfig.tabs.length >= 2)

  // Build a set of ticket IDs that are explicitly assigned to at least one tab
  const assignedTicketIds = useMemo(() => {
    if (!hasDayTabs || !dayTabConfig) return new Set<string>()
    const ids = new Set<string>()
    for (const tab of dayTabConfig.tabs) {
      for (const id of tab.ticketTypeIds) {
        ids.add(id)
      }
    }
    return ids
  }, [hasDayTabs, dayTabConfig])

  // Each tab's set of applicable dates (YYYY-MM-DD), derived from the
  // applicableDates of the ticket types assigned to it.
  const tabDates = useMemo(() => {
    if (!hasDayTabs || !dayTabConfig) return [] as Set<string>[]
    return dayTabConfig.tabs.map((tab) => {
      const set = new Set<string>()
      const idSet = new Set(tab.ticketTypeIds)
      for (const tt of ticketTypes) {
        if (idSet.has(tt.id) && Array.isArray(tt.applicableDates)) {
          for (const d of tt.applicableDates) {
            if (typeof d === 'string' && d) set.add(d.slice(0, 10))
          }
        }
      }
      return set
    })
  }, [hasDayTabs, dayTabConfig, ticketTypes])

  // Today's date in the event timezone (falls back to browser-local).
  const today = useMemo(() => {
    try {
      return timezone ? toDateInTz(new Date(), timezone) : new Date().toISOString().slice(0, 10)
    } catch {
      return new Date().toISOString().slice(0, 10)
    }
  }, [timezone])

  // Tabs still relevant: those with a date today-or-later. Tabs with no date
  // info are always kept. If every dated tab is past, keep them all so a
  // fully-elapsed event still shows something rather than blanking out.
  const visibleTabIndices = useMemo(() => {
    if (!hasDayTabs || !dayTabConfig) return [] as number[]
    const all = dayTabConfig.tabs.map((_, i) => i)
    const visible = all.filter((i) => {
      const dates = tabDates[i]
      if (!dates || dates.size === 0) return true
      return Array.from(dates).some((d) => d >= today)
    })
    return visible.length > 0 ? visible : all
  }, [hasDayTabs, dayTabConfig, tabDates, today])

  const [activeTab, setActiveTab] = useState<number>(0)
  const didInitTab = useRef(false)

  // Default to today's tab (or the first upcoming/visible tab) once tabs+dates
  // are known; also move off a tab that has become hidden (its day passed).
  useEffect(() => {
    if (!hasDayTabs || !dayTabConfig || visibleTabIndices.length === 0) return
    if (!didInitTab.current) {
      const todayIdx = visibleTabIndices.find((i) => tabDates[i]?.has(today))
      setActiveTab(todayIdx ?? visibleTabIndices[0])
      didInitTab.current = true
      return
    }
    if (activeTab !== -1 && !visibleTabIndices.includes(activeTab)) {
      setActiveTab(visibleTabIndices[0])
    }
  }, [hasDayTabs, dayTabConfig, visibleTabIndices, tabDates, today, activeTab])

  // Filter tickets based on active tab
  const filteredTickets = useMemo(() => {
    if (!hasDayTabs || !dayTabConfig) return ticketTypes

    if (activeTab === -1) {
      // "All" tab — show everything
      return ticketTypes
    }

    const tab = dayTabConfig.tabs[activeTab]
    if (!tab) return ticketTypes

    const tabIdSet = new Set(tab.ticketTypeIds)

    // Show tickets assigned to this tab + tickets not assigned to ANY tab (global)
    return ticketTypes.filter(tt =>
      tabIdSet.has(tt.id) || !assignedTicketIds.has(tt.id)
    )
  }, [ticketTypes, hasDayTabs, dayTabConfig, activeTab, assignedTicketIds])

  useEffect(() => {
    const parentHasSelections = Object.values(selectedTickets).some(q => q > 0)
    if (!parentHasSelections) {
      setQuantities({})
    }
  }, [selectedTickets])

  if (!ticketTypes || ticketTypes.length === 0) {
    return (
      <div className="py-8 text-center text-text-tertiary text-sm">
        No tickets available
      </div>
    )
  }

  const updateQuantity = (ticketTypeId: string, change: number) => {
    const ticket = ticketTypes.find(t => t.id === ticketTypeId)
    if (!ticket) return

    const current = quantities[ticketTypeId] || 0
    let next = current + change

    const min = ticket.minQuantity ?? 1
    const max = pickerMax(ticket)
    const availMax = ticket.available > 0 ? Math.min(max, ticket.available) : max

    // For shared slot capacity: cap total across all ticket types
    let effectiveMax = availMax
    if (slotCapacity !== undefined && slotCapacity >= 0) {
      const totalOther = Object.entries(quantities)
        .filter(([id]) => id !== ticketTypeId)
        .reduce((sum, [, qty]) => sum + qty, 0)
      const slotMax = Math.max(0, slotCapacity - totalOther)
      effectiveMax = Math.min(effectiveMax, slotMax)
    }
    // A sponsor link's limit is per ORDER: 3 VIP + 1 hospitality is 4 of 4.
    if (orderLimit != null && orderLimit > 0) {
      const totalOther = Object.entries(quantities)
        .filter(([id]) => id !== ticketTypeId)
        .reduce((sum, [, qty]) => sum + qty, 0)
      effectiveMax = Math.min(effectiveMax, Math.max(0, orderLimit - totalOther))
    }

    // Jump to minimum on first increment from zero
    if (change > 0 && current === 0) {
      next = min
    }
    // Drop to zero if decrementing below minimum
    if (change < 0 && next > 0 && next < min) {
      next = 0
    }

    next = Math.min(next, effectiveMax)
    next = Math.max(0, next)

    const newQuantities = { ...quantities, [ticketTypeId]: next }
    setQuantities(newQuantities)
    onQuantityChange(ticketTypeId, newQuantities[ticketTypeId])
  }

  return (
    <div>
      {hasDayTabs && dayTabConfig && (
        <div className="flex flex-wrap gap-1.5 pb-4">
          {visibleTabIndices.map((idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveTab(idx)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                activeTab === idx
                  ? 'bg-primary text-text-inverse shadow-sm'
                  : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary'
              }`}
            >
              {dayTabConfig.tabs[idx].label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setActiveTab(-1)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
              activeTab === -1
                ? 'bg-primary text-text-inverse shadow-sm'
                : 'bg-surface-secondary text-text-secondary hover:bg-surface-tertiary'
            }`}
          >
            All
          </button>
        </div>
      )}

      <div className="divide-y divide-surface-border">
      {filteredTickets.map((ticket, index) => {
        const quantity = quantities[ticket.id] || 0
        const totalSelected = Object.values(quantities).reduce((sum, qty) => sum + qty, 0)
        const isMemberLocked = !!ticket.memberLocked
        // Off-sale = outside its own sale window (not the same as sold out — it
        // may still have stock). Blocks the stepper + shows a status badge.
        const saleStatus = ticket.saleStatus ?? 'on_sale'
        const offSale = saleStatus !== 'on_sale'
        const offSaleLabel = ticketSaleStatusLabel(saleStatus)
        const isSoldOut = (!offSale && !ticket.isAvailable && ticket.available === 0) || (slotCapacity !== undefined && slotCapacity <= 0)
        // Use slot capacity (per-slot remaining) when available, otherwise fall back to global ticket available
        const effectiveAvail = slotCapacity !== undefined ? slotCapacity : ticket.available
        const avail = effectiveAvail
        const isCritical = !isSoldOut && !isMemberLocked && avail > 0 && avail <= 3
        const isLowStock = !isSoldOut && !isMemberLocked && !isCritical && avail > 0 && avail <= 10

        return (
          <div
            key={ticket.id}
            className={`py-5 ${isSoldOut || isMemberLocked || offSale ? 'opacity-60' : ''} ${ticket.backgroundColor ? 'border-l-4 pl-3 -ml-1' : ''}`}
            // A ticket given a color (e.g. VIP) carries it as an accent bar.
            style={ticket.backgroundColor ? { borderLeftColor: ticket.backgroundColor } : undefined}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-text-primary text-base">
                    {ticket.name}
                  </span>
                  {isMemberLocked && <Badge variant="neutral">Members Only</Badge>}
                  {offSale && !isMemberLocked && offSaleLabel && <Badge variant="neutral">{offSaleLabel}</Badge>}
                  {isSoldOut && !isMemberLocked && !offSale && <Badge variant="error">Sold Out</Badge>}
                  {ticket.showRemainingQuantity && isCritical && <Badge variant="error">Only {avail} left</Badge>}
                  {ticket.showRemainingQuantity && isLowStock && <Badge variant="warning">{avail} left</Badge>}
                  {ticket.badgeText && <Badge variant="primary">{ticket.badgeText}</Badge>}
                  {ticket.issuePerDay && (ticket.applicableDates?.length || 0) > 1 ? (
                    <Badge variant="primary">{ticket.applicableDates.length}-Day Pass</Badge>
                  ) : ticket.maxScans && ticket.maxScans > 1 && (
                    <Badge variant="primary">{ticket.maxScans}-Day Pass</Badge>
                  )}
                </div>
                {ticket.includesPerks?.length > 0 && (
                  <ul className="mt-1.5 space-y-0.5" data-ticket-perks>
                    {ticket.includesPerks.map(perk => (
                      <li key={perk} className="flex items-start gap-1.5 text-xs text-text-secondary">
                        <svg className="w-3.5 h-3.5 mt-px flex-shrink-0 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{perk}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {ticket.description && (
                  <ExpandableDescription html={ticket.description} />
                )}
                {ticket.applicableDates && ticket.applicableDates.length > 0 && (
                  <div className="text-xs text-text-tertiary mt-1">
                    Valid: {ticket.applicableDates.map(d => {
                      const dt = new Date(d + 'T12:00:00')
                      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }).join(', ')}
                    {ticket.issuePerDay && ticket.applicableDates.length > 1 && ' · one ticket per day'}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  {ticket.discountedAllInPrice != null && ticket.originalAllInPrice != null ? (
                    <>
                      <span className="text-lg font-bold text-text-primary">
                        {ticket.discountedAllInPrice > 0 ? formatPrice(ticket.discountedAllInPrice) : 'Free'}
                      </span>
                      <span className="text-sm text-text-tertiary line-through">
                        {formatPrice(ticket.originalAllInPrice)}
                      </span>
                    </>
                  ) : (
                    <span className="text-lg font-bold text-text-primary">
                      {ticket.allInPrice > 0 ? formatPrice(ticket.allInPrice) : 'Free'}
                    </span>
                  )}
                  {ticket.discountedAllInPrice == null && ticket.allInPrice > 0 && ticket.allInPrice !== ticket.price && (
                    <span className="text-xs text-text-tertiary">
                      inc. fees
                    </span>
                  )}
                  {ticket.minQuantity > 1 && (
                    <span className="text-xs text-text-tertiary">
                      Min. {ticket.minQuantity}
                    </span>
                  )}
                  {ticket.maxPerCustomer != null && (ticket.maxPerCustomer < (ticket.maxQuantity || 10) || ticket.perCustomerAlready > 0 || (quantity > 0 && quantity >= pickerMax(ticket))) && (
                    <span className="text-xs text-text-tertiary">
                      Limit {ticket.maxPerCustomer} per customer{ticket.perCustomerAlready > 0 ? ` (you have ${ticket.perCustomerAlready})` : ''}
                    </span>
                  )}
                </div>
              </div>

              {isMemberLocked && (
                <div className="flex-shrink-0 flex flex-col items-center gap-1">
                  <svg className="w-5 h-5 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <a href="/accounts/login" className="text-xs text-primary hover:underline whitespace-nowrap">Sign in</a>
                </div>
              )}

              {!isSoldOut && !isMemberLocked && !offSale && (
                <div className="flex items-center gap-2.5 flex-shrink-0">
                  <button
                    onClick={() => updateQuantity(ticket.id, -1)}
                    disabled={quantity === 0}
                    aria-label={`Decrease ${ticket.name} quantity`}
                    className="w-10 h-10 rounded-xl border border-surface-border flex items-center justify-center text-text-secondary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 active:scale-90"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                    </svg>
                  </button>

                  <span className="w-10 text-center text-lg font-bold text-text-primary tabular-nums">
                    {quantity}
                  </span>

                  <button
                    onClick={() => updateQuantity(ticket.id, 1)}
                    disabled={(avail > 0 && quantity >= avail) || quantity >= pickerMax(ticket) || (slotCapacity !== undefined && totalSelected >= slotCapacity) || (orderLimit != null && orderLimit > 0 && totalSelected >= orderLimit)}
                    aria-label={`Increase ${ticket.name} quantity`}
                    className="w-10 h-10 rounded-xl border border-surface-border flex items-center justify-center text-text-secondary hover:border-primary hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-all duration-200 active:scale-90"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      })}
      </div>
    </div>
  )
}

export default TicketSelector
